import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const BarbecueIntegration = ({ barbecue }) => {
  const [isParticipating, setIsParticipating] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  if (!barbecue?.isActive) return null;

  const handleParticipation = (participating) => {
    setIsParticipating(participating);
    // Handle barbecue participation logic
    console.log(`Barbecue participation: ${participating}`);
  };

  const handleContribution = () => {
    // Handle contribution assignment
    console.log('Handle contribution assignment');
  };

  return (
    <div className="bg-gradient-to-br from-orange-50 to-red-50 border border-orange-200 rounded-lg shadow-elevation-2 p-4">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center">
            <Icon name="Utensils" size={20} className="text-white" />
          </div>
          <div>
            <h3 className="font-heading font-semibold text-foreground">
              {barbecue?.title}
            </h3>
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <Icon name="MapPin" size={14} />
              <span>{barbecue?.location}</span>
              <Icon name="Clock" size={14} className="ml-2" />
              <span>{barbecue?.time}</span>
            </div>
          </div>
        </div>

        <Button
          variant="ghost"
          size="sm"
          iconName={showDetails ? 'ChevronUp' : 'ChevronDown'}
          onClick={() => setShowDetails(!showDetails)}
        />
      </div>

      {/* Participation Status */}
      <div className="flex items-center justify-between mb-4 p-3 bg-white/80 rounded-lg border border-orange-200/50">
        <div className="flex items-center space-x-2">
          <Icon 
            name={isParticipating ? 'CheckCircle' : 'Clock'} 
            size={16} 
            className={isParticipating ? 'text-success' : 'text-warning'}
          />
          <span className="text-sm font-medium text-foreground">
            {isParticipating ? 'Você vai participar' : 'Confirme sua participação'}
          </span>
        </div>

        <div className="flex items-center space-x-1">
          <Icon name="Users" size={14} className="text-muted-foreground" />
          <span className="text-sm text-muted-foreground">
            {barbecue?.participants} participantes
          </span>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <Button
          variant={isParticipating ? "outline" : "default"}
          size="sm"
          iconName="Check"
          iconPosition="left"
          onClick={() => handleParticipation(true)}
          className={isParticipating ? "border-success text-success hover:bg-success/10" : "bg-orange-500 hover:bg-orange-600 text-white"}
          fullWidth
        >
          {isParticipating ? 'Participando' : 'Participar'}
        </Button>

        <Button
          variant="outline"
          size="sm"
          iconName="DollarSign"
          iconPosition="left"
          onClick={handleContribution}
          fullWidth
          className="border-orange-300 text-orange-700 hover:bg-orange-50"
        >
          Ver Contribuição
        </Button>
      </div>

      {/* Detailed Information */}
      {showDetails && (
        <div className="space-y-4 pt-4 border-t border-orange-200">
          {/* Cost Information */}
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-3 bg-white/60 rounded-lg">
              <div className="text-lg font-bold text-orange-600">
                R$ {barbecue?.costPerPerson?.toFixed(2)}
              </div>
              <div className="text-xs text-muted-foreground">
                Custo por pessoa
              </div>
            </div>

            <div className="text-center p-3 bg-white/60 rounded-lg">
              <div className="text-lg font-bold text-orange-600">
                R$ {(barbecue?.costPerPerson * barbecue?.participants)?.toFixed(2)}
              </div>
              <div className="text-xs text-muted-foreground">
                Total arrecadado
              </div>
            </div>
          </div>

          {/* Menu */}
          <div>
            <h4 className="font-medium text-foreground mb-2 flex items-center">
              <Icon name="ChefHat" size={16} className="mr-2 text-orange-500" />
              Menu do Churrasco
            </h4>
            <div className="flex flex-wrap gap-2">
              {barbecue?.menu?.map((item, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-1 rounded-md bg-orange-100 text-orange-800 text-xs font-medium"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>

          {/* Organizer Info */}
          <div className="flex items-center justify-between p-3 bg-white/60 rounded-lg">
            <div>
              <p className="text-sm font-medium text-foreground">
                Organizado por
              </p>
              <p className="text-sm text-muted-foreground">
                {barbecue?.organizer}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              iconName="MessageCircle"
              onClick={() => {
                // Contact organizer
                console.log('Contact barbecue organizer');
              }}
            >
              Contatar
            </Button>
          </div>

          {/* Contribution Breakdown */}
          <div>
            <h4 className="font-medium text-foreground mb-2 flex items-center">
              <Icon name="Calculator" size={16} className="mr-2 text-orange-500" />
              Divisão de Custos
            </h4>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Carnes e acompanhamentos</span>
                <span className="font-medium text-foreground">R$ 25,00</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Bebidas</span>
                <span className="font-medium text-foreground">R$ 8,00</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Carvão e utensílios</span>
                <span className="font-medium text-foreground">R$ 2,00</span>
              </div>
              <div className="border-t border-orange-200 pt-2 mt-2">
                <div className="flex items-center justify-between text-sm font-medium">
                  <span className="text-foreground">Total por pessoa</span>
                  <span className="text-orange-600">R$ {barbecue?.costPerPerson?.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Status for Barbecue */}
          {isParticipating && (
            <div className="p-3 bg-warning/10 border border-warning/20 rounded-lg">
              <div className="flex items-start space-x-2">
                <Icon name="AlertTriangle" size={16} className="text-warning mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-warning mb-1">
                    Pagamento pendente
                  </p>
                  <p className="text-xs text-muted-foreground mb-2">
                    Confirme o pagamento do churrasco junto ao organizador
                  </p>
                  <Button
                    variant="warning"
                    size="sm"
                    iconName="CreditCard"
                    iconPosition="left"
                    onClick={() => {
                      // Handle barbecue payment
                      console.log('Handle barbecue payment');
                    }}
                  >
                    Pagar Churrasco
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Not Participating State */}
      {!isParticipating && !showDetails && (
        <div className="text-center py-2">
          <p className="text-sm text-muted-foreground">
            Participe do churrasco pós-pelada e celebre com a galera! 🍖
          </p>
        </div>
      )}
    </div>
  );
};

export default BarbecueIntegration;